import type { ParseJSONObject } from '@ikegps/core-api';
import type { IkeFetchableBase } from './fetchable';
import type { IkeCacheObject } from './objects/base';

export const IkeRestCacheFactory = {
	create<T>(obj: IkeCacheObject<T>): IkeRestCache<T> {
		return new IkeRestCacheBasic<T>(obj);
	},
};

export interface IkeRestCache<T = ParseJSONObject> {
	get<K extends keyof T>(key: K, value: string): IkeFetchableBase<T>;
	set<K extends keyof T>(key: K, value: string, obj: IkeFetchableBase<T>): void;
	has<K extends keyof T>(key: K, value: string): boolean;
}

export class IkeRestCacheBasic<T> implements IkeRestCache<T> {
	obj: IkeCacheObject<T>;
	cache: { [key: string]: IkeFetchableBase<T> };

	constructor(obj: IkeCacheObject<T>) {
		this.obj = obj;
		this.cache = {};
	}

	getKey<K extends keyof T>(key: K, value: string): string {
		if (this.obj.canCache(key)) {
			return `${key as string}:${value}`;
		}
		throw new Error(`Invalid cacheKey ${this.obj.actionName}:${key as string}`);
	}

	get<K extends keyof T>(key: K, value: string): IkeFetchableBase<T> {
		const internalKey = this.getKey(key, value);
		return this.cache[internalKey];
	}

	set<K extends keyof T>(key: K, value: string, obj: IkeFetchableBase<T>) {
		const internalKey = this.getKey(key, value);
		this.cache[internalKey] = obj;
	}

	has<K extends keyof T>(key: K, value: string): boolean {
		const internalKey = this.getKey(key, value);
		return this.cache[internalKey] != null;
	}
}

export class IkeRestCacheControl {
	_caches: { [key: string]: IkeRestCache<any> };

	constructor() {
		this._caches = {};
	}

	get<T, K extends keyof T>(obj: IkeCacheObject<T>, key: K): IkeRestCache<T> {
		if (this._caches[obj.actionName] == null) {
			this._caches[obj.actionName] = IkeRestCacheFactory.create(obj);
		}
		const cache = this._caches[obj.actionName];
		if (!obj.canCache(key)) {
			throw new Error(`Cannot get one on non cached key: ${obj.actionName}.${key as string}`);
		}
		return cache;
	}

	clear<T>(obj?: IkeCacheObject<T>) {
		if (obj != null) {
			this._caches[obj.actionName] = null;
		} else {
			this._caches = {};
		}
	}
}
